import { GET_LIST, GET_MANY, UPDATE, GET_ONE } from 'react-admin';
import _ from 'lodash';
import settings from '../settings';
import feathersClient from './feathersClient';
import axios from 'axios';
import { normalizeMediaUrls } from '../utils/sanitizeS3Url';

const getAdminApiBaseUrl = () => {
    const environment = process.env.REACT_APP_ENV || 'development';
    if (environment === 'production') {
        return 'https://admin-api.boxitstorage.com';
    } else if (environment === 'staging') {
        return 'https://admin-api.staging.boxitstorage.com';
    } else if (environment === 'development') {
        return 'http://localhost:3000';
    }
    const normalizedBaseUrl = settings.apiUrl?.replace(/\/$/, '');
    return normalizedBaseUrl || 'https://admin-api.staging.boxitstorage.com';
};

const toNumericId = (value: unknown) => {
    const numericId = Number(value);
    return Number.isFinite(numericId) ? numericId : null;
};

// Define interfaces for the request bodies
// Define interfaces for the request bodies to match backend DTOs

// Interface for storages list request body
interface StoragesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    statuses?: string[];
    partnerId?: number;
    partnerIds?: number[];
    userId?: number;
    userIds?: number[];
    storagetypeId?: number;
    storagetypeIds?: number[];
    countryIds?: number[];
    parentIds?: number[];
    isPart?: boolean;
    roomId?: number;
    roomName?: string;
    warehouseIds?: number[];
    searchTerm?: string;
    storagetypeCodes?: string[];
    orderIds?: number[];
}

interface StorageRecordsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    storageIds?: number[];
    warehouseIds?: number[];
    locationIds?: number[];
    statuses?: string[];
    types?: string[];
    orderIds?: number[];
    userIds?: number[];
    countryIds?: number[];
    searchTerm?: string;
}

interface StorageTypesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    codes?: string[];
    names?: string[];
    partnerIds?: number[];
    countryIds?: number[];
    storageStatuses?: string[];
    userIds?: number[];
    inventory?: boolean;
    searchTerm?: string;
}

interface BoxesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    codes?: string[];
    searchTerm?: string;
    userIds?: number[];
    partnerIds?: number[];
    countryIds?: number[];
    warehouseIds?: number[];
    statuses?: string[];
}

interface RequestsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    statuses?: string[];
    types?: string[];
    userIds?: (number | string)[];
    partnerIds?: (number | string)[];
    countryIds?: (number | string)[];
    cityIds?: (number | string)[];
    assigneeIds?: (number | string)[];
    teamIds?: (number | string)[];
    warehouseIds?: (number | string)[];
    orderIds?: (number | string)[];
    dates?: {
        from?: string;
        to?: string;
    };
    searchTerm?: string;
    chargeTypes?: string;
    ids?: (number | string)[];
    excludeIds?: boolean;
    excludeLines?: boolean;
}

interface RequestLinesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: (number | string)[];
    requestIds?: (number | string)[];
    storageIds?: (number | string)[];
    orderIds?: (number | string)[];
    statuses?: string[];
    searchTerm?: string;
}

interface WarehousesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    cityIds?: (number | string)[];
    countryIds?: (number | string)[];
    isActive?: boolean;
    searchTerm?: string;
}

interface OrdersListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    statuses?: string[];
    orderTypes?: string[];
    userIds?: (number | string)[];
    partnerIds?: (number | string)[];
    countryIds?: (number | string)[];
    cityIds?: (number | string)[];
    assigneeIds?: (number | string)[];
    orderIds?: (number | string)[];
    sources?: string[];
    chargeMethods?: string[];
    createdDateRange?: {
        from?: string;
        to?: string;
    };
    orderDateRange?: {
        from?: string;
        to?: string;
    };
    renewalDateRange?: {
        from?: string;
        to?: string;
    };
    quoteSentAtRange?: {
        from?: string;
        to?: string;
    };
    searchTerm?: string;
}

interface UsersListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: (number | string)[];
    assigneeIds?: (number | string)[];
    partnerIds?: (number | string)[];
    countryIds?: (number | string)[];
    tagsOr?: (number | string)[];
    tagsAnd?: (number | string)[];
    statuses?: string[];
    userTypes?: string[];
    excludeUserTypes?: string[];
    userRoles?: string[];
    createdDateRange?: {
        from?: string;
        to?: string;
    };
    searchTerm?: string;
    source?: string;
}

interface PartnersListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    countryIds?: number[];
    userIds?: number[];
    searchTerm?: string;
}

interface CancelReasonsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    searchTerm?: string;
}

interface CountriesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    searchTerm?: string;
}

interface CitiesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    countryIds?: number[];
    searchTerm?: string;
}

interface InvoicesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: (number | string)[];
    statuses?: string[];
    types?: string[];
    partnerIds?: (number | string)[];
    userIds?: (number | string)[];
    countryIds?: (number | string)[];
    orderIds?: (number | string)[];
    dates?: {
        from?: string;
        to?: string;
    };
    dueDates?: {
        from?: string;
        to?: string;
    };
    searchTerm?: string;
}

interface ChargesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: (number | string)[];
    types?: string[];
    userIds?: (number | string)[];
    partnerIds?: (number | string)[];
    countryIds?: (number | string)[];
    orderIds?: (number | string)[];
    requestIds?: (number | string)[];
    invoiceIds?: (number | string | null)[];
    dates?: {
        from?: string;
        to?: string;
    };
    searchTerm?: string;
}

interface CommentsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    model: string;
    documentId: number | string;
}

interface RevisionsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    model: string;
    documentId: number | string;
}

interface CommunicationRecordsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    resourceType: string;
    resourceId: number | string;
}

interface CardsListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    searchTerm?: string;
    modes?: string[];
    statuses?: string[];
    userIds?: number[];
    partnerIds?: number[];
    countryIds?: number[];
    cardHolderName?: string;
    cardNumber?: string;
    tokenName?: string;
    dates?: {
        from?: string;
        to?: string;
    };
}

interface AddressesListRequestBody {
    limit: number;
    page: number;
    sortBy: string;
    sortOrder: string;
    ids?: number[];
    partnerIds?: number[];
    countryIds?: number[];
    userIds?: number[];
    cityIds?: number[];
    createdDateRange?: {
        from?: string;
        to?: string;
    };
    searchTerm?: string;
}

// Helper function to modify URL parameters
const modifyUrl = (url, oldParam, newParam) => {
    return url.replace(
        new RegExp(`(\?|&)${encodeURIComponent(oldParam)}=`, 'g'),
        `$1${encodeURIComponent(newParam)}=`
    );
};

const customMapper = dataProvider => async (verb, resource, params) => {
    // Map leads resource to order resource for the custom API endpoint
    let originalResource = resource;
    if (resource === 'leads') {
        resource = 'orders';
    }
    //only to remove select id from ids array from select all storages
    if (resource === 'storages' && params.ids) {
        params.ids = params.ids.filter(id => id !== 'select');
    } else if (
        resource === 'requests' &&
        params &&
        params.data &&
        params.data.request_lines
    ) {
        params.data.request_lines = params.data.request_lines.filter(
            e => e.storageId !== 'select'
        );
    }

    // Handle room.name filter for storages to be sent as roomName
    if (
        resource === 'storages' &&
        verb === GET_LIST &&
        params.filter &&
        params.filter['room.name']
    ) {
        console.log('Transforming room.name filter to roomName');

        // Create a new params object with the modified filter
        const newParams = {
            ...params,
            filter: {
                ...params.filter,
                // Add the roomName parameter with the value from room.name
                roomName: params.filter['room.name'],
            },
        };

        // Delete the original room.name filter
        delete newParams.filter['room.name'];

        // Use the modified params
        params = newParams;
    }

    if (
        (resource === 'users' ||
            resource === 'addresses' ||
            resource === 'partners' ||
            resource === 'cancel-reasons' ||
            resource === 'country' ||
            resource === 'city') &&
        verb === GET_MANY &&
        params.ids
    ) {
        try {
            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const chunks = _.chunk(params.ids, 20);

            const results = await Promise.all(
                chunks.map(async ids => {
                    const numericIds = ids
                        .map(id => toNumericId(id))
                        .filter((id): id is number => id !== null);

                    if (!numericIds.length) {
                        return [];
                    }

                    const requestBody = {
                        limit: numericIds.length || 20,
                        page: 1,
                        sortBy: 'id',
                        sortOrder: 'DESC',
                        ids: numericIds,
                    };

                    const response = await fetch(
                        `${baseUrl}/api/v1/${resource}/list`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                ...(token
                                    ? { Authorization: `Bearer ${token}` }
                                    : {}),
                            },
                            body: JSON.stringify(requestBody),
                        }
                    );

                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    const result = await response.json();
                    return result.results || [];
                })
            );

            return {
                data: results.flat(),
            };
        } catch (error) {
            console.error(`Error fetching ${resource} getMany:`, error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'users' && verb === GET_ONE) {
        try {
            const userId = toNumericId(params.id);

            if (userId === null) {
                return dataProvider(verb, resource, params);
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/users/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    limit: 1,
                    page: 1,
                    sortBy: 'id',
                    sortOrder: 'DESC',
                    ids: [userId],
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results && result.results[0]) || {},
            };
        } catch (error) {
            console.error('Error fetching users getOne:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Handle pagination for queries with multiple IDs
    if (
        (resource === 'users' || resource === 'addresses') &&
        verb === GET_MANY &&
        params.ids
    ) {
        // Split IDs into chunks of 20
        const chunks = _.chunk(params.ids, 20);
        // Make multiple requests and combine results
        const results = await Promise.all(
            chunks.map(async ids => {
                const chunkParams = { ...params, ids };
                const response = await dataProvider(
                    verb,
                    resource,
                    chunkParams
                );
                return response.data;
            })
        );
        // Combine all results
        return {
            data: results.flat(),
        };
    }

    if (resource === 'orders' && verb === GET_MANY && params.ids) {
        try {
            const numericIds = params.ids
                .map(id => toNumericId(id))
                .filter((id): id is number => id !== null);

            if (!numericIds.length) {
                return {
                    data: [],
                };
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/orders/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    limit: numericIds.length,
                    page: 1,
                    sortBy: 'id',
                    sortOrder: 'DESC',
                    orderIds: numericIds,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results || [],
            };
        } catch (error) {
            console.error('Error fetching orders getMany:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for requests list
    if (
        resource === 'requests' &&
        (verb === GET_LIST || verb === 'GET_MANY_REFERENCE')
    ) {
        // Use the custom requests/list endpoint
        try {
            console.log('paramsparams', params);
            console.log('filter values:', params.filter);
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: RequestsListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order,
            };

            // We'll only add the dates object when date filters are applied

            // Add filters if they exist
            if (params.filter) {
                // Handle search term
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                // Handle status filter
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle type filter
                if (params.filter.type) {
                    requestBody.types = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId
                        : [params.filter.countryId];
                }

                // Handle city filter
                if (params.filter.cityId) {
                    requestBody.cityIds = Array.isArray(params.filter.cityId)
                        ? params.filter.cityId
                        : [params.filter.cityId];
                }

                // Handle assignee filter
                if (params.filter.assigneeId) {
                    requestBody.assigneeIds = Array.isArray(
                        params.filter.assigneeId
                    )
                        ? params.filter.assigneeId
                        : [params.filter.assigneeId];
                }

                // Handle team filter
                if (params.filter.teamId) {
                    requestBody.teamIds = Array.isArray(params.filter.teamId)
                        ? params.filter.teamId
                        : [params.filter.teamId];
                }

                // Handle warehouse filter
                if (params.filter.warehouseId) {
                    requestBody.warehouseIds = Array.isArray(
                        params.filter.warehouseId
                    )
                        ? params.filter.warehouseId
                        : [params.filter.warehouseId];
                }

                // Handle order filter
                if (params.filter.orderId) {
                    requestBody.orderIds = Array.isArray(params.filter.orderId)
                        ? params.filter.orderId
                        : [params.filter.orderId];
                }

                // Handle date range filter (unified date filter for both created and scheduled dates)
                // Check for both nested date objects and direct date[$gte] format
                console.log(
                    'Date filter structure:',
                    JSON.stringify(params.filter.date)
                );

                const hasNestedDateFilter =
                    params.filter.date &&
                    (params.filter.date.$gte || params.filter.date.$lte);

                const hasDirectDateFilter =
                    params.filter['createdAt[$gte]'] ||
                    params.filter['createdAt[$lte]'] ||
                    params.filter['scheduledDate[$gte]'] ||
                    params.filter['scheduledDate[$lte]'] ||
                    params.filter['date[$gte]'] ||
                    params.filter['date[$lte]'];

                const hasSimpleDateFilter =
                    params.filter.date &&
                    typeof params.filter.date === 'string';

                console.log('hasNestedDateFilter:', hasNestedDateFilter);
                console.log('hasDirectDateFilter:', hasDirectDateFilter);
                console.log('hasSimpleDateFilter:', hasSimpleDateFilter);

                if (
                    hasNestedDateFilter ||
                    hasDirectDateFilter ||
                    hasSimpleDateFilter
                ) {
                    // Only create the dates object when date filters are applied
                    const datesObj: { from?: string; to?: string } = {};

                    // Handle simple date string format (Today, Tomorrow)
                    if (
                        params.filter.date &&
                        typeof params.filter.date === 'string'
                    ) {
                        datesObj.from = `${params.filter.date}T00:00:00Z`;
                        datesObj.to = `${params.filter.date}T23:59:59Z`;
                    }
                    // Handle nested date object format
                    else if (params.filter.date && params.filter.date.$gte) {
                        datesObj.from = `${params.filter.date.$gte}T00:00:00Z`;
                    }

                    if (params.filter.date && params.filter.date.$lte) {
                        datesObj.to = `${params.filter.date.$lte}T23:59:59Z`;
                    }

                    // Handle direct date[$gte] format if nested format wasn't found
                    if (!datesObj.from) {
                        // Prioritize createdAt dates if both are provided
                        if (params.filter['createdAt[$gte]']) {
                            datesObj.from = `${params.filter['createdAt[$gte]']}T00:00:00Z`;
                        } else if (params.filter['scheduledDate[$gte]']) {
                            datesObj.from = `${params.filter['scheduledDate[$gte]']}T00:00:00Z`;
                        } else if (params.filter['date[$gte]']) {
                            datesObj.from = `${params.filter['date[$gte]']}T00:00:00Z`;
                        }
                    }

                    if (!datesObj.to) {
                        if (params.filter['createdAt[$lte]']) {
                            datesObj.to = `${params.filter['createdAt[$lte]']}T23:59:59Z`;
                        } else if (params.filter['scheduledDate[$lte]']) {
                            datesObj.to = `${params.filter['scheduledDate[$lte]']}T23:59:59Z`;
                        } else if (params.filter['date[$lte]']) {
                            datesObj.to = `${params.filter['date[$lte]']}T23:59:59Z`;
                        }
                    }

                    // Only add the dates object if we have at least one date filter value
                    if (datesObj.from || datesObj.to) {
                        requestBody.dates = datesObj;
                    }
                }

                // Handle charge types filter
                if (params.filter.chargeTypes) {
                    requestBody.chargeTypes = params.filter.chargeTypes;
                }

                // Handle specific IDs if provided
                if (params.filter.ids) {
                    requestBody.ids = Array.isArray(params.filter.ids)
                        ? params.filter.ids
                        : [params.filter.ids];
                }

                // Handle exclude options if provided
                if (params.filter.excludeIds !== undefined) {
                    requestBody.excludeIds = params.filter.excludeIds;
                }

                if (params.filter.excludeLines !== undefined) {
                    requestBody.excludeLines = params.filter.excludeLines;
                }
            }

            // Determine the base URL based on environment
            const baseUrl = getAdminApiBaseUrl();

            // Log the final request body being sent to the API
            console.log(
                'Final request body:',
                JSON.stringify(requestBody, null, 2)
            );

            // Make the API request
            const response = await axios.post(
                `${baseUrl}/api/v1/requests/list`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'feathers-jwt'
                        )}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Format the response to match react-admin's expected format
            return {
                data: response.data.results.map((item, idx) => ({
                    ...item,
                    serial: (page - 1) * perPage + idx + 1,
                })),
                total: response.data.totalCount,
            };
        } catch (error) {
            console.error('Error fetching requests list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    if (
        resource === 'request-lines' &&
        (verb === GET_LIST || verb === 'GET_MANY_REFERENCE')
    ) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'createdAt',
                order: 'DESC',
            };

            const requestBody: RequestLinesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            if (
                params.target &&
                params.id !== undefined &&
                params.id !== null
            ) {
                const relationIds = Array.isArray(params.id)
                    ? params.id
                    : [params.id];

                if (params.target === 'requestId') {
                    requestBody.requestIds = relationIds;
                }

                if (params.target === 'storageId') {
                    requestBody.storageIds = relationIds;
                }

                if (params.target === 'orderId') {
                    requestBody.orderIds = relationIds;
                }
            }

            if (params.filter) {
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id
                        : [params.filter.id];
                }

                if (params.filter.requestId) {
                    requestBody.requestIds = Array.isArray(
                        params.filter.requestId
                    )
                        ? params.filter.requestId
                        : [params.filter.requestId];
                }

                if (params.filter.storageId) {
                    requestBody.storageIds = Array.isArray(
                        params.filter.storageId
                    )
                        ? params.filter.storageId
                        : [params.filter.storageId];
                }

                if (params.filter.orderId) {
                    requestBody.orderIds = Array.isArray(params.filter.orderId)
                        ? params.filter.orderId
                        : [params.filter.orderId];
                }

                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(
                `${baseUrl}/api/v1/request-lines/list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results || []).map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching request lines list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for comments list
    if (
        resource === 'comments' &&
        (verb === GET_LIST || verb === 'GET_MANY_REFERENCE')
    ) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 25,
            };
            const { field, order } = params.sort || {
                field: 'createdAt',
                order: 'ASC',
            };

            const requestBody: CommentsListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'DESC' ? 'DESC' : 'ASC',
                model: params.filter?.model,
                documentId: params.filter?.documentId,
            };

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/comments/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results || []).map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching comments list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for revisions list
    if (
        resource === 'revisions' &&
        (verb === GET_LIST || verb === 'GET_MANY_REFERENCE')
    ) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 25,
            };
            const { field, order } = params.sort || {
                field: 'createdAt',
                order: 'DESC',
            };

            const requestBody: RevisionsListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
                model: params.filter?.model,
                documentId: params.filter?.documentId,
            };

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/revisions/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results || []).map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching revisions list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'Communication-records' && verb === 'GET_MANY_REFERENCE') {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 25,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            const requestBody: CommunicationRecordsListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
                resourceType: params.filter?.resourceType,
                resourceId: params.filter?.resourceId,
            };

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(
                `${baseUrl}/api/v1/communication-records/list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results || []).map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching communication records list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for warehouses list
    if (resource === 'warehouses' && verb === GET_LIST) {
        // Use the custom warehouses/list endpoint
        try {
            const { page, perPage } =
                params.pagination ||
                {
                    // page: 1,
                    // perPage: 10,
                };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'ASC',
            };

            // Prepare request body
            const requestBody: WarehousesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order,
                // Not filtering by active status to show all warehouses
            };

            // Add filters if they exist
            if (params.filter) {
                // Handle search term
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId
                        : [params.filter.countryId];
                }

                // Handle city filter
                if (params.filter.cityId) {
                    requestBody.cityIds = Array.isArray(params.filter.cityId)
                        ? params.filter.cityId
                        : [params.filter.cityId];
                }

                // Handle active status filter
                if (params.filter.isActive !== undefined) {
                    requestBody.isActive = params.filter.isActive;
                }
            }

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            // Make the API request
            const response = await axios.post(
                `${baseUrl}/api/v1/warehouses/list`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'feathers-jwt'
                        )}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Format the response to match react-admin's expected format
            return {
                data: response.data.results.map((item, idx) => ({
                    ...item,
                    serial: (page - 1) * perPage + idx + 1,
                })),
                total: response.data.totalCount,
            };
        } catch (error) {
            console.error('Error fetching warehouses list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for storage records list
    if (resource === 'storage-records' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: StorageRecordsListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order.toUpperCase(),
            };

            // Add filters if provided
            if (params.filter) {
                // Handle search term
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                // Handle status filter
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle type filter
                if (params.filter.type) {
                    requestBody.types = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                // Handle storage filter
                if (params.filter.storageId) {
                    requestBody.storageIds = Array.isArray(
                        params.filter.storageId
                    )
                        ? params.filter.storageId
                        : [params.filter.storageId];
                }

                // Handle warehouse filter
                if (params.filter.warehouseId) {
                    requestBody.warehouseIds = Array.isArray(
                        params.filter.warehouseId
                    )
                        ? params.filter.warehouseId
                        : [params.filter.warehouseId];
                }

                // Handle location filter
                if (params.filter.locationId) {
                    requestBody.locationIds = Array.isArray(
                        params.filter.locationId
                    )
                        ? params.filter.locationId
                        : [params.filter.locationId];
                }

                // Handle order filter
                if (params.filter.orderId) {
                    requestBody.orderIds = Array.isArray(params.filter.orderId)
                        ? params.filter.orderId
                        : [params.filter.orderId];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId
                        : [params.filter.countryId];
                }
            }

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            // Make the API request
            const response = await fetch(
                `${baseUrl}/api/v1/storages/records/list`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'feathers-jwt'
                        )}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            const result = await response.json();

            // Return data in the format expected by react-admin
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching storage records list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for boxes list
    if (resource === 'boxes' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: BoxesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order.toUpperCase(),
            };

            // Add filters if provided
            if (params.filter) {
                // Handle search term
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId
                        : [params.filter.countryId];
                }

                // Handle warehouse filter
                if (params.filter.warehouseId) {
                    requestBody.warehouseIds = Array.isArray(
                        params.filter.warehouseId
                    )
                        ? params.filter.warehouseId
                        : [params.filter.warehouseId];
                }

                // Handle status filter
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle code filter
                if (params.filter.code) {
                    requestBody.codes = Array.isArray(params.filter.code)
                        ? params.filter.code
                        : [params.filter.code];
                }
            }

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            // Make the API request
            const response = await fetch(`${baseUrl}/api/v1/boxes/list`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                        'feathers-jwt'
                    )}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await response.json();

            // Return data in the format expected by react-admin
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching boxes list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for storage types list
    if (resource === 'storage-types' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: StorageTypesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order.toUpperCase(),
            };

            // Add filters if provided
            if (params.filter) {
                // Handle search term
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle inventory filter
                if (params.filter.inventory !== undefined) {
                    requestBody.inventory = params.filter.inventory;
                }

                // Handle status filter
                if (params.filter.status) {
                    requestBody.storageStatuses = Array.isArray(
                        params.filter.status
                    )
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle code filter
                if (params.filter.code) {
                    requestBody.codes = Array.isArray(params.filter.code)
                        ? params.filter.code
                        : [params.filter.code];
                }

                // Handle name filter
                if (params.filter.name) {
                    requestBody.names = Array.isArray(params.filter.name)
                        ? params.filter.name
                        : [params.filter.name];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId
                        : [params.filter.countryId];
                }
            }

            const baseUrl = getAdminApiBaseUrl();

            // Make the API request
            const response = await fetch(
                `${baseUrl}/api/v1/storages/types/list`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'feathers-jwt'
                        )}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            const result = await response.json();

            // Return data in the format expected by react-admin
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching storage types list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'storage-types' && verb === GET_MANY && params.ids) {
        try {
            const numericIds = params.ids
                .map(id => toNumericId(id))
                .filter((id): id is number => id !== null);

            if (!numericIds.length) {
                return {
                    data: [],
                };
            }

            const baseUrl = getAdminApiBaseUrl();
            const response = await fetch(
                `${baseUrl}/api/v1/storages/types/list`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'feathers-jwt'
                        )}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        limit: numericIds.length,
                        page: 1,
                        sortBy: 'id',
                        sortOrder: 'DESC',
                        ids: numericIds,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results || [],
            };
        } catch (error) {
            console.error('Error fetching storage types getMany:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'storage-types' && verb === GET_ONE) {
        try {
            const storageTypeId = toNumericId(params.id);

            if (storageTypeId === null) {
                return dataProvider(verb, resource, params);
            }

            const baseUrl = getAdminApiBaseUrl();
            const response = await fetch(
                `${baseUrl}/api/v1/storages/types/list`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'feathers-jwt'
                        )}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        limit: 1,
                        page: 1,
                        sortBy: 'id',
                        sortOrder: 'DESC',
                        ids: [storageTypeId],
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results && result.results[0]) || {},
            };
        } catch (error) {
            console.error('Error fetching storage types getOne:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for users list, staff-users list, and non-staff-users list
    if (
        (resource === 'users' ||
            resource === 'staff-users' ||
            resource === 'non-staff-users') &&
        verb === GET_LIST
    ) {
        // Check if this is a staff users list request or a non-staff users list request
        const isStaffUsersList =
            params.filter && params.filter.basePath === '/staff-users';
        const isNonStaffUsersList =
            params.filter && params.filter.basePath === '/users';

        // Remove basePath from filter as it's not needed for the API
        if ((isStaffUsersList || isNonStaffUsersList) && params.filter) {
            delete params.filter.basePath;
        }
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: UsersListRequestBody = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            // Add staff filter by default for staff users list
            if (isStaffUsersList) {
                requestBody.userTypes = ['staff'];
            }

            // Add filters if provided
            if (params.filter) {
                // Handle status filter
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle type filter
                if (params.filter.type) {
                    requestBody.userTypes = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                // Handle exclude type filter (for non-staff users)
                // Only apply this filter if it's not a staff users list
                if (!isStaffUsersList && params.filter['type[$ne]']) {
                    requestBody.excludeUserTypes = Array.isArray(
                        params.filter['type[$ne]']
                    )
                        ? params.filter['type[$ne]']
                        : [params.filter['type[$ne]']];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = [params.filter.countryId];
                }

                // Handle assignee filter
                if (params.filter.assigneeId) {
                    requestBody.assigneeIds = Array.isArray(
                        params.filter.assigneeId
                    )
                        ? params.filter.assigneeId
                        : [params.filter.assigneeId];
                }

                // Handle tags filters
                if (params.filter.tagsor) {
                    requestBody.tagsOr = Array.isArray(params.filter.tagsor)
                        ? params.filter.tagsor
                        : [params.filter.tagsor];
                }

                if (params.filter.tagsand) {
                    requestBody.tagsAnd = Array.isArray(params.filter.tagsand)
                        ? params.filter.tagsand
                        : [params.filter.tagsand];
                }

                // Handle userRoles filter
                if (params.filter.userRoles) {
                    requestBody.userRoles = Array.isArray(
                        params.filter.userRoles
                    )
                        ? params.filter.userRoles
                        : [params.filter.userRoles];
                }

                // Handle date range filters
                // Created date range
                if (
                    params.filter['createdAt[$gte]'] ||
                    params.filter['createdAt[$lte]'] ||
                    (params.filter.createdAt &&
                        (params.filter.createdAt.$gte ||
                            params.filter.createdAt.$lte))
                ) {
                    requestBody.createdDateRange = {};

                    // Handle flattened format
                    if (params.filter['createdAt[$gte]']) {
                        requestBody.createdDateRange.from =
                            params.filter['createdAt[$gte]'];
                    }
                    if (params.filter['createdAt[$lte]']) {
                        requestBody.createdDateRange.to =
                            params.filter['createdAt[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.createdAt) {
                        if (params.filter.createdAt.$gte) {
                            requestBody.createdDateRange.from =
                                params.filter.createdAt.$gte;
                        }
                        if (params.filter.createdAt.$lte) {
                            requestBody.createdDateRange.to =
                                params.filter.createdAt.$lte;
                        }
                    }
                }

                // Handle source filter
                if (params.filter.source) {
                    requestBody.source = params.filter.source;
                }

                // Handle search query
                if (params.filter.q) requestBody.searchTerm = params.filter.q;

                // Handle specific user IDs
                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }
            }

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            const apiUrl = `${baseUrl}/api/v1/users/list`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('feathers-jwt')
                        ? `Bearer ${localStorage.getItem('feathers-jwt')}`
                        : '',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Format the response to match react-admin's expected format
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching users list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'partners' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };
            const requestBody: PartnersListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            if (params.filter) {
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }

                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId.map(id => Number(id))
                        : [Number(params.filter.countryId)];
                }

                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId.map(id => Number(id))
                        : [Number(params.filter.userId)];
                }
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/partners/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching partners list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'partners' && verb === GET_ONE) {
        try {
            const partnerId = toNumericId(params.id);

            if (partnerId === null) {
                return dataProvider(verb, resource, params);
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/partners/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    limit: 1,
                    page: 1,
                    sortBy: 'id',
                    sortOrder: 'DESC',
                    ids: [partnerId],
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results && result.results[0]) || {},
            };
        } catch (error) {
            console.error('Error fetching partners getOne:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'cancel-reasons' && verb === GET_LIST) {
        try {
            const page = params.pagination?.page ?? 1;
            const perPage = params.pagination?.perPage ?? 10;
            const field = params.sort?.field ?? 'id';
            const order = params.sort?.order ?? 'DESC';
            const requestBody: CancelReasonsListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            if (params.filter) {
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(
                `${baseUrl}/api/v1/cancel-reasons/list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching cancel reasons list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'cancel-reasons' && verb === GET_ONE) {
        try {
            const cancelReasonId = toNumericId(params.id);

            if (cancelReasonId === null) {
                return dataProvider(verb, resource, params);
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(
                `${baseUrl}/api/v1/cancel-reasons/list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        limit: 1,
                        page: 1,
                        sortBy: 'id',
                        sortOrder: 'DESC',
                        ids: [cancelReasonId],
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results && result.results[0]) || {},
            };
        } catch (error) {
            console.error('Error fetching cancel reasons getOne:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'country' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 25,
            };
            const { field, order } = params.sort || {
                field: 'name',
                order: 'ASC',
            };
            const requestBody: CountriesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'DESC' ? 'DESC' : 'ASC',
            };

            if (params.filter) {
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/country/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching countries list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'country' && verb === GET_ONE) {
        try {
            const countryId = toNumericId(params.id);

            if (countryId === null) {
                return dataProvider(verb, resource, params);
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/country/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    limit: 1,
                    page: 1,
                    sortBy: 'id',
                    sortOrder: 'ASC',
                    ids: [countryId],
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results && result.results[0]) || {},
            };
        } catch (error) {
            console.error('Error fetching country getOne:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'city' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 25,
            };
            const { field, order } = params.sort || {
                field: 'name',
                order: 'ASC',
            };
            const requestBody: CitiesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'DESC' ? 'DESC' : 'ASC',
            };

            if (params.filter) {
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }

                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId.map(id => Number(id))
                        : [Number(params.filter.countryId)];
                }
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/city/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching cities list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'city' && verb === GET_ONE) {
        try {
            const cityId = toNumericId(params.id);

            if (cityId === null) {
                return dataProvider(verb, resource, params);
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(`${baseUrl}/api/v1/city/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    limit: 1,
                    page: 1,
                    sortBy: 'id',
                    sortOrder: 'ASC',
                    ids: [cityId],
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: (result.results && result.results[0]) || {},
            };
        } catch (error) {
            console.error('Error fetching city getOne:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for orders and leads list
    // Use custom API endpoint for storages list
    if (resource === 'storages' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'ASC',
            };
            const filters = { ...params.filter };

            // Handle room.name filter for storages to be sent as roomName
            if (filters && filters['room.name']) {
                filters.roomName = filters['room.name'];
                delete filters['room.name'];
            }

            // Prepare request body for the custom API
            const requestBody: StoragesListRequestBody = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            // Add filters if they exist
            if (filters) {
                // Handle search term
                if (filters.q) {
                    requestBody.searchTerm = filters.q;
                }

                // Handle status filter
                if (filters.status) {
                    requestBody.statuses = Array.isArray(filters.status)
                        ? filters.status
                        : [filters.status];
                }

                // Handle statuses filter (directly from SelectStorageInput)
                if (filters.statuses) {
                    console.log(
                        'Processing statuses filter:',
                        filters.statuses
                    );
                    requestBody.statuses = Array.isArray(filters.statuses)
                        ? filters.statuses
                        : [filters.statuses];
                }

                // Handle partner filter
                if (filters.partnerId) {
                    // Use partnerIds property as an array format and ensure all values are numbers
                    requestBody.partnerIds = Array.isArray(filters.partnerId)
                        ? filters.partnerId.map(id => Number(id))
                        : [Number(filters.partnerId)];
                    // Remove the old property to avoid confusion
                    delete requestBody.partnerId;
                }

                // Handle user filter
                if (filters.userId) {
                    // Use userIds property as an array format and ensure all values are numbers
                    requestBody.userIds = Array.isArray(filters.userId)
                        ? filters.userId.map(id => Number(id))
                        : [Number(filters.userId)];
                    // Remove the old property to avoid confusion
                    delete requestBody.userId;
                }

                // Handle storage type filter
                if (filters.storagetypeId) {
                    // Use storagetypeIds property that we added to the interface and ensure all values are numbers
                    (requestBody as any).storagetypeIds = Array.isArray(
                        filters.storagetypeId
                    )
                        ? filters.storagetypeId.map(id => Number(id))
                        : [Number(filters.storagetypeId)];
                    // Remove the old property to avoid confusion
                    delete requestBody.storagetypeId;
                }

                // Handle isPart filter
                if (filters.isPart !== undefined) {
                    requestBody.isPart = filters.isPart;
                }

                // Handle country filter
                if (filters.countryId) {
                    // Use countryIds property as an array format and ensure all values are numbers
                    requestBody.countryIds = Array.isArray(filters.countryId)
                        ? filters.countryId.map(id => Number(id))
                        : [Number(filters.countryId)];
                }

                // Handle parent filter
                if (filters.parentId) {
                    // Support parentId as scalar, array, or {$in: [...]}
                    const rawParentIds = Array.isArray(filters.parentId)
                        ? filters.parentId
                        : filters.parentId &&
                          Array.isArray(filters.parentId.$in)
                        ? filters.parentId.$in
                        : [filters.parentId];

                    const normalizedParentIds = rawParentIds
                        .map(id => Number(id))
                        .filter(id => !Number.isNaN(id));

                    if (normalizedParentIds.length) {
                        requestBody.parentIds = normalizedParentIds;
                    }
                }

                // Handle storagetypeCodes filter
                if (filters.storagetypeCodes) {
                    requestBody.storagetypeCodes = Array.isArray(
                        filters.storagetypeCodes
                    )
                        ? filters.storagetypeCodes
                        : [filters.storagetypeCodes];
                }

                // Handle orderIds filter
                if (filters.orderId) {
                    // Ensure all values are numbers
                    requestBody.orderIds = Array.isArray(filters.orderId)
                        ? filters.orderId.map(id => Number(id))
                        : [Number(filters.orderId)];
                }

                // Handle room filter
                if (filters.roomId) {
                    requestBody.roomId = filters.roomId;
                }

                // Handle roomName filter (already handled above)
                if (filters.roomName) {
                    requestBody.roomName = filters.roomName;
                }

                // Handle warehouse filter
                if (filters.warehouseId) {
                    requestBody.warehouseIds = Array.isArray(
                        filters.warehouseId
                    )
                        ? filters.warehouseId.map(id => Number(id))
                        : [Number(filters.warehouseId)];
                }

                // Handle specific IDs if provided
                if (filters.ids) {
                    requestBody.ids = Array.isArray(filters.ids)
                        ? filters.ids.map(id => Number(id))
                        : [Number(filters.ids)];
                }
            }

            // Determine API URL based on environment
            const apiBaseUrl =
                process.env.REACT_APP_ENV === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';
            const apiUrl = `${apiBaseUrl}/api/v1/storages/list`;

            // Get auth token from localStorage
            const token = localStorage.getItem('feathers-jwt');
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            };

            // Make API request
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(requestBody),
            });
            const result = await response.json();

            // Format response for react-admin
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching storages list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    if ((resource === 'orders' || resource === 'leads') && verb === GET_LIST) {
        // Use the custom orders/list endpoint
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: OrdersListRequestBody = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            // Add filter for statuses if not explicitly provided
            if (params.filter) {
                // Handle status filter
                if (params.filter.status) {
                    // If status is an array, use it directly
                    if (Array.isArray(params.filter.status)) {
                        requestBody.statuses = params.filter.status;
                    } else {
                        requestBody.statuses = [params.filter.status];
                    }
                } else if (
                    !params.filter.userId &&
                    originalResource === 'orders'
                ) {
                    // Default statuses if not specified and no userId filter - only for orders, not for leads
                    requestBody.statuses = [
                        'confirmed',
                        'complete',
                        'cancelled',
                    ];
                    // requestBody.statuses = []; // Default to no statuses if not specified and no userId filter
                }

                // Handle other filters
                // Handle type filter
                if (params.filter.type) {
                    requestBody.orderTypes = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    // Send a single countryId instead of an array
                    requestBody.countryIds = [params.filter.countryId];
                }

                // Handle city filter
                if (params.filter.cityId) {
                    // Send a single cityId instead of an array
                    requestBody.cityIds = [params.filter.cityId];
                }

                // Handle assignee filter
                if (params.filter.assigneeId) {
                    requestBody.assigneeIds = Array.isArray(
                        params.filter.assigneeId
                    )
                        ? params.filter.assigneeId
                        : [params.filter.assigneeId];
                }

                // Handle orderIds filter
                if (params.filter.id) {
                    // Convert order IDs to numbers to meet API validation requirements
                    requestBody.orderIds = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }

                // Handle date range filters
                // Created date range
                if (
                    params.filter['createdAt[$gte]'] ||
                    params.filter['createdAt[$lte]'] ||
                    (params.filter.createdAt &&
                        (params.filter.createdAt.$gte ||
                            params.filter.createdAt.$lte))
                ) {
                    requestBody.createdDateRange = {};

                    // Handle flattened format
                    if (params.filter['createdAt[$gte]']) {
                        requestBody.createdDateRange.from =
                            params.filter['createdAt[$gte]'];
                    }
                    if (params.filter['createdAt[$lte]']) {
                        requestBody.createdDateRange.to =
                            params.filter['createdAt[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.createdAt) {
                        if (params.filter.createdAt.$gte) {
                            requestBody.createdDateRange.from =
                                params.filter.createdAt.$gte;
                        }
                        if (params.filter.createdAt.$lte) {
                            requestBody.createdDateRange.to =
                                params.filter.createdAt.$lte;
                        }
                    }
                }

                // Order date range
                if (
                    params.filter['date[$gte]'] ||
                    params.filter['date[$lte]'] ||
                    (params.filter.date &&
                        (params.filter.date.$gte || params.filter.date.$lte))
                ) {
                    requestBody.orderDateRange = {};

                    // Handle flattened format
                    if (params.filter['date[$gte]']) {
                        requestBody.orderDateRange.from =
                            params.filter['date[$gte]'];
                    }
                    if (params.filter['date[$lte]']) {
                        requestBody.orderDateRange.to =
                            params.filter['date[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.date) {
                        if (params.filter.date.$gte) {
                            requestBody.orderDateRange.from =
                                params.filter.date.$gte;
                        }
                        if (params.filter.date.$lte) {
                            requestBody.orderDateRange.to =
                                params.filter.date.$lte;
                        }
                    }
                }

                // Renewal date range
                if (
                    params.filter['renewalDate[$gte]'] ||
                    params.filter['renewalDate[$lte]'] ||
                    (params.filter.renewalDate &&
                        (params.filter.renewalDate.$gte ||
                            params.filter.renewalDate.$lte))
                ) {
                    requestBody.renewalDateRange = {};

                    // Handle flattened format
                    if (params.filter['renewalDate[$gte]']) {
                        requestBody.renewalDateRange.from =
                            params.filter['renewalDate[$gte]'];
                    }
                    if (params.filter['renewalDate[$lte]']) {
                        requestBody.renewalDateRange.to =
                            params.filter['renewalDate[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.renewalDate) {
                        if (params.filter.renewalDate.$gte) {
                            requestBody.renewalDateRange.from =
                                params.filter.renewalDate.$gte;
                        }
                        if (params.filter.renewalDate.$lte) {
                            requestBody.renewalDateRange.to =
                                params.filter.renewalDate.$lte;
                        }
                    }
                }

                // Quote sent at date range (for leads)
                console.log('🔍 LEADS DEBUG: Checking quoteSentAt filters...');
                console.log(
                    '🔍 LEADS DEBUG: quoteSentAt[$gte]:',
                    params.filter['quoteSentAt[$gte]']
                );
                console.log(
                    '🔍 LEADS DEBUG: quoteSentAt[$lte]:',
                    params.filter['quoteSentAt[$lte]']
                );
                console.log(
                    '🔍 LEADS DEBUG: quoteSentAt object:',
                    params.filter.quoteSentAt
                );

                if (
                    params.filter['quoteSentAt[$gte]'] ||
                    params.filter['quoteSentAt[$lte]'] ||
                    (params.filter.quoteSentAt &&
                        (params.filter.quoteSentAt.$gte ||
                            params.filter.quoteSentAt.$lte))
                ) {
                    console.log(
                        '🔍 LEADS DEBUG: QuoteSentAt filters found! Creating quoteSentAtRange object...'
                    );
                    requestBody.quoteSentAtRange = {};

                    // Handle flattened format
                    if (params.filter['quoteSentAt[$gte]']) {
                        console.log(
                            '🔍 LEADS DEBUG: Setting quoteSentAtRange.from from flattened format:',
                            params.filter['quoteSentAt[$gte]']
                        );
                        requestBody.quoteSentAtRange.from =
                            params.filter['quoteSentAt[$gte]'];
                    }
                    if (params.filter['quoteSentAt[$lte]']) {
                        console.log(
                            '🔍 LEADS DEBUG: Setting quoteSentAtRange.to from flattened format:',
                            params.filter['quoteSentAt[$lte]']
                        );
                        requestBody.quoteSentAtRange.to =
                            params.filter['quoteSentAt[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.quoteSentAt) {
                        if (params.filter.quoteSentAt.$gte) {
                            console.log(
                                '🔍 LEADS DEBUG: Setting quoteSentAtRange.from from nested format:',
                                params.filter.quoteSentAt.$gte
                            );
                            requestBody.quoteSentAtRange.from =
                                params.filter.quoteSentAt.$gte;
                        }
                        if (params.filter.quoteSentAt.$lte) {
                            console.log(
                                '🔍 LEADS DEBUG: Setting quoteSentAtRange.to from nested format:',
                                params.filter.quoteSentAt.$lte
                            );
                            requestBody.quoteSentAtRange.to =
                                params.filter.quoteSentAt.$lte;
                        }
                    }
                }

                // Handle source filter
                if (params.filter.source) {
                    requestBody.sources = Array.isArray(params.filter.source)
                        ? params.filter.source
                        : [params.filter.source];
                }

                // Handle charge method filter
                if (params.filter.chargeMethod) {
                    requestBody.chargeMethods = Array.isArray(
                        params.filter.chargeMethod
                    )
                        ? params.filter.chargeMethod
                        : [params.filter.chargeMethod];
                }

                // Handle search query
                if (params.filter.q) requestBody.searchTerm = params.filter.q;
            } else {
                // Default statuses if no filter provided
                requestBody.statuses = ['confirmed', 'complete', 'cancelled'];
                // requestBody.statuses = []; // Empty array to include all statuses
            }

            // Use dynamic API URL based on environment
            // For debugging
            console.log('Request body:', JSON.stringify(requestBody, null, 2));

            const baseUrl = getAdminApiBaseUrl();
            const apiUrl = `${baseUrl}/api/v1/orders/list`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('feathers-jwt')
                        ? `Bearer ${localStorage.getItem('feathers-jwt')}`
                        : '',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
                originalResource: originalResource,
            };
        } catch (error) {
            console.error('Error fetching orders list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Handle custom invoices list API endpoint
    if (
        resource === 'invoices' &&
        (verb === GET_LIST || verb === 'GET_MANY_REFERENCE')
    ) {
        try {
            const { page, perPage } = params.pagination;
            const { field, order } = params.sort;
            const originalResource = resource;

            const requestBody: InvoicesListRequestBody = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            if (params.filter) {
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                if (params.filter.type) {
                    requestBody.types = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                if (params.filter.countryId) {
                    requestBody.countryIds = [params.filter.countryId];
                }

                if (params.filter.orderId) {
                    requestBody.orderIds = Array.isArray(params.filter.orderId)
                        ? params.filter.orderId
                        : [params.filter.orderId];
                }

                if (
                    params.filter['date[$gte]'] ||
                    params.filter['date[$lte]'] ||
                    (params.filter.date &&
                        (params.filter.date.$gte || params.filter.date.$lte))
                ) {
                    requestBody.dates = {};

                    if (params.filter['date[$gte]']) {
                        requestBody.dates.from = params.filter['date[$gte]'];
                    }
                    if (params.filter['date[$lte]']) {
                        requestBody.dates.to = params.filter['date[$lte]'];
                    }

                    if (params.filter.date) {
                        if (params.filter.date.$gte) {
                            requestBody.dates.from = params.filter.date.$gte;
                        }
                        if (params.filter.date.$lte) {
                            requestBody.dates.to = params.filter.date.$lte;
                        }
                    }
                }

                if (
                    params.filter['dueDate[$gte]'] ||
                    params.filter['dueDate[$lte]'] ||
                    (params.filter.dueDate &&
                        (params.filter.dueDate.$gte ||
                            params.filter.dueDate.$lte))
                ) {
                    requestBody.dueDates = {};

                    if (params.filter['dueDate[$gte]']) {
                        requestBody.dueDates.from =
                            params.filter['dueDate[$gte]'];
                    }
                    if (params.filter['dueDate[$lte]']) {
                        requestBody.dueDates.to =
                            params.filter['dueDate[$lte]'];
                    }

                    if (params.filter.dueDate) {
                        if (params.filter.dueDate.$gte) {
                            requestBody.dueDates.from =
                                params.filter.dueDate.$gte;
                        }
                        if (params.filter.dueDate.$lte) {
                            requestBody.dueDates.to =
                                params.filter.dueDate.$lte;
                        }
                    }
                }

                if (params.filter.q) requestBody.searchTerm = params.filter.q;
            }

            console.log(
                'Invoice request body:',
                JSON.stringify(requestBody, null, 2)
            );

            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            const apiUrl = `${baseUrl}/api/v1/invoices/list`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('feathers-jwt')
                        ? `Bearer ${localStorage.getItem('feathers-jwt')}`
                        : '',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
                originalResource: originalResource,
            };
        } catch (error) {
            console.error('Error fetching invoices list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    // Handle custom cards list API endpoint
    if (resource === 'cards' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'createdAt',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: CardsListRequestBody = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            // Add filters if provided
            if (params.filter) {
                // Handle status filter
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle mode filter
                if (params.filter.mode) {
                    requestBody.modes = Array.isArray(params.filter.mode)
                        ? params.filter.mode
                        : [params.filter.mode];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId.map(id => Number(id))
                        : [Number(params.filter.userId)];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId.map(id => Number(id))
                        : [Number(params.filter.partnerId)];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId.map(id => Number(id))
                        : [Number(params.filter.countryId)];
                }

                // Handle card holder name filter
                if (params.filter.cardHolderName) {
                    requestBody.cardHolderName = params.filter.cardHolderName;
                }

                // Handle card number filter
                if (params.filter.cardNumber) {
                    requestBody.cardNumber = params.filter.cardNumber;
                }

                // Handle token name filter
                if (params.filter.tokenName) {
                    requestBody.tokenName = params.filter.tokenName;
                }

                // Handle date range filters
                if (
                    params.filter['createdAt[$gte]'] ||
                    params.filter['createdAt[$lte]'] ||
                    (params.filter.createdAt &&
                        (params.filter.createdAt.$gte ||
                            params.filter.createdAt.$lte))
                ) {
                    requestBody.dates = {};

                    // Handle flattened format
                    if (params.filter['createdAt[$gte]']) {
                        requestBody.dates.from =
                            params.filter['createdAt[$gte]'];
                    }
                    if (params.filter['createdAt[$lte]']) {
                        requestBody.dates.to = params.filter['createdAt[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.createdAt) {
                        if (params.filter.createdAt.$gte) {
                            requestBody.dates.from =
                                params.filter.createdAt.$gte;
                        }
                        if (params.filter.createdAt.$lte) {
                            requestBody.dates.to = params.filter.createdAt.$lte;
                        }
                    }
                }

                // Handle search query
                if (params.filter.q) requestBody.searchTerm = params.filter.q;

                // Handle specific card IDs
                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }
            }

            // For debugging
            console.log(
                'Cards request body:',
                JSON.stringify(requestBody, null, 2)
            );

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            const apiUrl = `${baseUrl}/api/v1/cards/list`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('feathers-jwt')
                        ? `Bearer ${localStorage.getItem('feathers-jwt')}`
                        : '',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Format the response to match react-admin's expected format
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching cards list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Handle custom payments list API endpoint
    if (resource === 'payments' && verb === GET_LIST) {
        try {
            const { page, perPage } = params.pagination;
            const { field, order } = params.sort;
            const originalResource = resource;

            // Create request body for the custom API
            const requestBody: any = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            // Add filters if provided
            if (params.filter) {
                console.log('Payments filter params:', params.filter);

                // Handle status filter
                if (params.filter.status) {
                    requestBody.statuses = Array.isArray(params.filter.status)
                        ? params.filter.status
                        : [params.filter.status];
                }

                // Handle type filter
                if (params.filter.type) {
                    requestBody.types = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                // Handle mode filter
                if (params.filter.mode) {
                    requestBody.modes = Array.isArray(params.filter.mode)
                        ? params.filter.mode
                        : [params.filter.mode];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = [params.filter.countryId];
                }

                // Handle method filter (data.response.eci) - support both nested and flattened formats
                let eciValue = null;

                // Check flattened format first
                if (params.filter['data.response.eci']) {
                    eciValue = params.filter['data.response.eci'];
                }
                // Check nested format
                else if (
                    params.filter.data &&
                    params.filter.data.response &&
                    params.filter.data.response.eci
                ) {
                    eciValue = params.filter.data.response.eci;
                }

                if (eciValue) {
                    console.log('Method filter detected:', eciValue);
                    requestBody.ecis = Array.isArray(eciValue)
                        ? eciValue
                        : [eciValue];
                    console.log(
                        'Setting ecis in requestBody:',
                        requestBody.ecis
                    );
                }

                // Handle date range filters for paidAt
                if (
                    params.filter['paidAt[$gte]'] ||
                    params.filter['paidAt[$lte]'] ||
                    (params.filter.paidAt &&
                        (params.filter.paidAt.$gte ||
                            params.filter.paidAt.$lte))
                ) {
                    requestBody.dates = {};

                    // Handle flattened format
                    if (params.filter['paidAt[$gte]']) {
                        requestBody.dates.from = params.filter['paidAt[$gte]'];
                    }
                    if (params.filter['paidAt[$lte]']) {
                        requestBody.dates.to = params.filter['paidAt[$lte]'];
                    }

                    // Handle nested format
                    if (params.filter.paidAt) {
                        if (params.filter.paidAt.$gte) {
                            requestBody.dates.from = params.filter.paidAt.$gte;
                        }
                        if (params.filter.paidAt.$lte) {
                            requestBody.dates.to = params.filter.paidAt.$lte;
                        }
                    }
                }

                // Handle search query
                if (params.filter.q) requestBody.searchTerm = params.filter.q;

                // Handle specific payment IDs
                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }

                // Handle invoice IDs filter
                if (params.filter.invoiceId) {
                    requestBody.invoiceIds = Array.isArray(
                        params.filter.invoiceId
                    )
                        ? params.filter.invoiceId.map(id => Number(id))
                        : [Number(params.filter.invoiceId)];
                }
            }

            // For debugging
            console.log(
                'Payments request body:',
                JSON.stringify(requestBody, null, 2)
            );

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            const apiUrl = `${baseUrl}/api/v1/payments/list`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('feathers-jwt')
                        ? `Bearer ${localStorage.getItem('feathers-jwt')}`
                        : '',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Format the response to match react-admin's expected format
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
                // Preserve the original resource name for proper UI handling
                originalResource: originalResource,
            };
        } catch (error) {
            console.error('Error fetching payments list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Handle custom charges list API endpoint
    if (
        resource === 'charges' &&
        (verb === GET_LIST || verb === 'GET_MANY_REFERENCE')
    ) {
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 100,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };
            const originalResource = resource;

            // Create request body for the custom API
            const requestBody: ChargesListRequestBody = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            if (
                params.target &&
                params.id !== undefined &&
                params.id !== null
            ) {
                const relationIds = Array.isArray(params.id)
                    ? params.id
                    : [params.id];

                if (params.target === 'orderId') {
                    requestBody.orderIds = relationIds;
                }

                if (params.target === 'requestId') {
                    requestBody.requestIds = relationIds;
                }

                if (params.target === 'invoiceId') {
                    requestBody.invoiceIds = relationIds;
                }
            }

            // Add filters if provided
            if (params.filter) {
                // Handle type filter
                if (params.filter.type) {
                    requestBody.types = Array.isArray(params.filter.type)
                        ? params.filter.type
                        : [params.filter.type];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId
                        : [params.filter.userId];
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId
                        : [params.filter.partnerId];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = [params.filter.countryId];
                }

                // Handle order filter
                if (params.filter.orderId) {
                    requestBody.orderIds = Array.isArray(params.filter.orderId)
                        ? params.filter.orderId
                        : [params.filter.orderId];
                }

                // Handle request filter
                if (params.filter.requestId) {
                    requestBody.requestIds = Array.isArray(
                        params.filter.requestId
                    )
                        ? params.filter.requestId
                        : [params.filter.requestId];
                }

                // Handle invoice filter
                if (params.filter.invoiceId) {
                    requestBody.invoiceIds = Array.isArray(
                        params.filter.invoiceId
                    )
                        ? params.filter.invoiceId
                        : [params.filter.invoiceId];
                }

                // Handle date range filters
                if (
                    params.filter['createdAt[$gte]'] ||
                    params.filter['createdAt[$lte]']
                ) {
                    requestBody.dates = {};

                    if (params.filter['createdAt[$gte]']) {
                        requestBody.dates.from =
                            params.filter['createdAt[$gte]'];
                    }
                    if (params.filter['createdAt[$lte]']) {
                        requestBody.dates.to = params.filter['createdAt[$lte]'];
                    }
                }

                // Handle search query
                if (params.filter.q) requestBody.searchTerm = params.filter.q;

                // Handle specific IDs if provided
                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id
                        : [params.filter.id];
                }
            }

            // For debugging
            console.log(
                'Charges request body:',
                JSON.stringify(requestBody, null, 2)
            );

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            const apiUrl = `${baseUrl}/api/v1/charges/list`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('feathers-jwt')
                        ? `Bearer ${localStorage.getItem('feathers-jwt')}`
                        : '',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Format the response to match react-admin's expected format
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
                // Preserve the original resource name for proper UI handling
                originalResource: originalResource,
            };
        } catch (error) {
            console.error('Error fetching charges list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    // Use custom API endpoint for addresses list
    if (resource === 'addresses' && verb === GET_LIST) {
        console.log(
            '🔍 ADDRESSES DEBUG: Using custom API endpoint for addresses list'
        );
        try {
            const { page, perPage } = params.pagination || {
                page: 1,
                perPage: 10,
            };
            const { field, order } = params.sort || {
                field: 'id',
                order: 'DESC',
            };

            // Prepare request body
            const requestBody: AddressesListRequestBody = {
                limit: perPage,
                page: page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            // Add filters if provided
            if (params.filter) {
                // Handle search term
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                // Handle partner filter
                if (params.filter.partnerId) {
                    requestBody.partnerIds = Array.isArray(
                        params.filter.partnerId
                    )
                        ? params.filter.partnerId.map(id => Number(id))
                        : [Number(params.filter.partnerId)];
                }

                // Handle user filter
                if (params.filter.userId) {
                    requestBody.userIds = Array.isArray(params.filter.userId)
                        ? params.filter.userId.map(id => Number(id))
                        : [Number(params.filter.userId)];
                }

                // Handle city filter
                if (params.filter.cityId) {
                    requestBody.cityIds = Array.isArray(params.filter.cityId)
                        ? params.filter.cityId.map(id => Number(id))
                        : [Number(params.filter.cityId)];
                }

                // Handle country filter
                if (params.filter.countryId) {
                    requestBody.countryIds = Array.isArray(
                        params.filter.countryId
                    )
                        ? params.filter.countryId.map(id => Number(id))
                        : [Number(params.filter.countryId)];
                }

                // Handle date range filters
                if (
                    params.filter['createdAt[$gte]'] ||
                    params.filter['createdAt[$lte]']
                ) {
                    requestBody.createdDateRange = {
                        from: params.filter['createdAt[$gte]'],
                        to: params.filter['createdAt[$lte]'],
                    };
                }
            }

            // Determine the base URL based on environment
            const environment = process.env.REACT_APP_ENV || 'development';
            const baseUrl =
                environment === 'production'
                    ? 'https://admin-api.boxitstorage.com'
                    : 'https://admin-api.staging.boxitstorage.com';

            // Make the API request
            const response = await fetch(`${baseUrl}/api/v1/addresses/list`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                        'feathers-jwt'
                    )}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Return data in the format expected by react-admin
            return {
                data: result.results.map((item, idx) => ({
                    serial: (page - 1) * perPage + idx + 1,
                    ...item,
                })),
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching addresses list:', error);
            // Fall back to the default data provider if the custom API fails
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'cancel-reasons' && verb === GET_LIST) {
        try {
            const page = params.pagination?.page ?? 1;
            const perPage = params.pagination?.perPage ?? 10;
            const field = params.sort?.field ?? 'id';
            const order = params.sort?.order ?? 'DESC';
            const requestBody: any = {
                limit: perPage,
                page,
                sortBy: field,
                sortOrder: order === 'ASC' ? 'ASC' : 'DESC',
            };

            if (params.filter) {
                if (params.filter.q) {
                    requestBody.searchTerm = params.filter.q;
                }

                if (params.filter.id) {
                    requestBody.ids = Array.isArray(params.filter.id)
                        ? params.filter.id.map(id => Number(id))
                        : [Number(params.filter.id)];
                }
            }

            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');
            const response = await fetch(
                `${baseUrl}/api/v1/cancel-reasons/list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return {
                data: result.results || [],
                total: result.totalCount || 0,
            };
        } catch (error) {
            console.error('Error fetching cancel reasons list:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (resource === 'orders' && verb === GET_ONE) {
        try {
            const orderId = params.id;
            const baseUrl = getAdminApiBaseUrl();
            const token = localStorage.getItem('feathers-jwt');

            const response = await axios.get(
                `${baseUrl}/api/v1/orders/${orderId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                }
            );

            return {
                data: {
                    ...response.data,
                    _partnerId:
                        response.data?._partnerId ?? response.data?.partnerId,
                },
            };
        } catch (error) {
            console.error('Error fetching order from admin API:', error);
            return dataProvider(verb, resource, params);
        }
    }

    if (verb === 'createMany') {
        return Promise.reject(`customMapper: Unknown dataProvider method`);
    }

    // Create a wrapper around the dataProvider to intercept and modify the request
    const originalProvider = async (...args) => {
        const [verbArg, resourceArg, paramsArg] = args;

        // Redirect PATCH requests for non-staff-users to the /users endpoint
        if (resourceArg === 'non-staff-users' && verbArg === UPDATE) {
            // Use the users resource instead of non-staff-users for updates
            return dataProvider(verbArg, 'users', paramsArg);
        }

        // Intercept the original provider to modify the URL for storages
        const originalFind = feathersClient.service('storages').find;

        if (
            resourceArg === 'storages' &&
            verbArg === 'GET_LIST' &&
            paramsArg.filter &&
            paramsArg.filter['room.name']
        ) {
            console.log(
                'Intercepting storages request to modify room.name parameter'
            );

            // Temporarily override the find method to modify the URL
            feathersClient.service('storages').find = async function (params) {
                // Call the original find method
                const result = await originalFind.call(this, params);

                // If the URL contains room.name, modify it to use roomName instead
                if (result.url && result.url.includes('room.name=')) {
                    result.url = modifyUrl(result.url, 'room.name', 'roomName');
                    console.log('Modified URL:', result.url);
                }

                return result;
            };
        }

        try {
            // Call the original data provider
            const response = await dataProvider(...args);

            // Restore the original find method
            if (resourceArg === 'storages') {
                feathersClient.service('storages').find = originalFind;
            }

            if (Array.isArray(response.data)) {
                response.data = response.data.map((item, idx) => {
                    const normalizedItem = normalizeMediaUrls(item);
                    return {
                        serial: response.skip + idx + 1,
                        ...normalizedItem,
                    };
                });
            } else if (response.data && typeof response.data === 'object') {
                response.data = normalizeMediaUrls(response.data);
            }

            // For debugging
            if (resourceArg === 'storages' && verbArg === 'GET_LIST') {
                console.log('Storages API request URL:', response.url);

                // Direct URL modification for room.name parameter
                if (response.url && response.url.includes('room.name=')) {
                    // Create a new URL object to manipulate the URL
                    const url = new URL(response.url);
                    const roomNameValue = url.searchParams.get('room.name');

                    if (roomNameValue) {
                        // Remove the old parameter and add the new one
                        url.searchParams.delete('room.name');
                        url.searchParams.append('roomName', roomNameValue);

                        // Update the URL in the response
                        response.url = url.toString();
                        console.log('Modified URL:', response.url);
                    }
                }
            }

            return response;
        } catch (error) {
            // Restore the original find method in case of error
            if (resourceArg === 'storages') {
                feathersClient.service('storages').find = originalFind;
            }

            // Check if error has a code property before accessing it
            if (
                error &&
                typeof error === 'object' &&
                'code' in error &&
                error.code === 403
            ) {
                // Forbidden
                return; // Makes sure the promise is resolved, so the chain continues
            }
            throw error;
        }
    };

    // Use custom API endpoint for charges list
    // if (resource === 'charges' && verb === GET_LIST) {
    //     try {
    //         const { page, perPage } = params.pagination || {
    //             page: 1,
    //             perPage: 10,
    //         };
    //         const { field, order } = params.sort || {
    //             field: 'id',
    //             order: 'DESC',
    //         };
    //         const filters = { ...params.filter };

    //         // Build the request body
    //         const requestBody: ChargesListRequestBody = {
    //             limit: perPage,
    //             page: page,
    //             sortBy: field,
    //             sortOrder: order,
    //         };

    //         // Handle search term
    //         if (filters.q) {
    //             requestBody.searchTerm = filters.q;
    //             delete filters.q;
    //         }

    //         // Handle type filter
    //         if (filters.type) {
    //             requestBody.types = Array.isArray(filters.type)
    //                 ? filters.type
    //                 : [filters.type];
    //         }

    //         // Handle user filter
    //         if (filters.userId) {
    //             requestBody.userIds = Array.isArray(filters.userId)
    //                 ? filters.userId.map(id => Number(id))
    //                 : [Number(filters.userId)];
    //         }

    //         // Handle partner filter
    //         if (filters.partnerId) {
    //             requestBody.partnerIds = Array.isArray(filters.partnerId)
    //                 ? filters.partnerId.map(id => Number(id))
    //                 : [Number(filters.partnerId)];
    //         }

    //         // Handle country filter
    //         if (filters.countryId) {
    //             requestBody.countryIds = Array.isArray(filters.countryId)
    //                 ? filters.countryId.map(id => Number(id))
    //                 : [Number(filters.countryId)];
    //         }

    //         // Handle invoice filter (status)
    //         if (filters.invoiceId) {
    //             if (filters.invoiceId === 'null') {
    //                 // For unassigned charges, we need to handle this specially
    //                 requestBody.invoiceIds = [null];
    //             } else {
    //                 requestBody.invoiceIds = Array.isArray(filters.invoiceId)
    //                     ? filters.invoiceId.map(id => Number(id))
    //                     : [Number(filters.invoiceId)];
    //             }
    //         }

    //         // Handle date range filters
    //         if (filters['createdAt[$gte]'] || filters['createdAt[$lte]']) {
    //             requestBody.dates = {
    //                 from: filters['createdAt[$gte]'],
    //                 to: filters['createdAt[$lte]'],
    //             };
    //         }

    //         // Determine the API URL based on environment
    //         const isProduction = window.location.hostname.includes('admin.boxitstorage.com');
    //         const baseUrl = isProduction
    //             ? 'https://admin-api.boxitstorage.com'
    //             : 'https://admin-api.staging.boxitstorage.com';
    //         const apiUrl = `${baseUrl}/api/v1/charges/list`;

    //         const response = await fetch(apiUrl, {
    //             method: 'POST',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 Authorization: localStorage.getItem('feathers-jwt')
    //                     ? `Bearer ${localStorage.getItem('feathers-jwt')}`
    //                     : '',
    //             },
    //             body: JSON.stringify(requestBody),
    //         });

    //         if (!response.ok) {
    //             throw new Error(`HTTP error! status: ${response.status}`);
    //         }

    //         const result = await response.json();

    //         // Return data in the format expected by react-admin
    //         return {
    //             data: result.results.map((item, idx) => ({
    //                 serial: (page - 1) * perPage + idx + 1,
    //                 ...item,
    //             })),
    //             total: result.totalCount || 0,
    //         };
    //     } catch (error) {
    //         console.error('Error fetching charges list:', error);
    //         // Fall back to the default data provider if the custom API fails
    //         return dataProvider(verb, resource, params);
    //     }
    // }

    return originalProvider(verb, resource, params);
};

export default customMapper;
